<template>
  <div
    class="ma-0 pa-0 pt-2 d-flex flex-row gap-2 pa-2"
    ref="container"
  >
    <material-categories-container :is-open.sync="drawerIsOpen">
      <material-categories
        :collapsable="!$vuetify.breakpoint.xsOnly"
        ref="categories"
        v-model="activeCategoryV"
        @input="$emit('update:active-category', $event)"
        :items="categories"
        :total="materialsTotalOverall"
        :loading="loadingCategories"
        :mandatory="mandatoryCategory"
        :class="{
          'col': !collapsedCategories,
          'col-2': !collapsedCategories,
        }"
        :collapsed-start="collapsedCategories"
        class="flex-grow-1"
        @toggle:collapse="onToggleCollapse"
        @scroll:last="fetchCategoriesNext"
      />
    </material-categories-container>

    <div
      class="ma-0 pa-0 gap-2 flex-grow-1 d-flex flex-column overflow-x-hidden"
    >
      <div class="ma-0 d-flex flex-row flex-shrink-0 flex-grow-0">
        <app-card class="grow pa-2 px-4 d-flex flex-row">
          <app-text-field
            v-model="searchV"
            @input="$emit('update:search', $event)"
            clearable
            placeholder="Search"
            prepend-inner-icon="mdi-magnify"
            dense
            class="search-bar"
            hide-details
          >
            <template #append>
              <app-tag
                v-if="searchTag"
                closable
                text-color="primary"
                class="primary--text"
                @click:close="resetSearchTag"
              >
                {{ searchTag }}
              </app-tag>
            </template>
          </app-text-field>

          <app-button
            icon
            main-icon="$vuetify.icons.refresh"
            class="ml-2"
            @click="onClickRefresh"
          >
          </app-button>

          <MaterialViewSettingsMenu
            :disabled="!zoomActive"
            @click:increase="$bus.$emit('zoom:increase')"
            @click:decrease="$bus.$emit('zoom:decrease')"
          />
        </app-card>
      </div>

      <app-card
        color="transparent"
        class="flex-grow-1 d-flex flex-column overflow-y-auto overflow-x-visible"
      >
        <slot></slot>
      </app-card>
    </div>
  </div>
</template>

<script>
  import MaterialCategories from '@/components/Materials/MaterialCategories';
  import AppTag from '@/components/AppTag';
  import MaterialViewSettingsMenu from './MaterialViewSettingsMenu';
  import requestsUtils from '@/utils/requests';
  import arraysUtils from '@/utils/arrays';
  import MaterialCategoriesContainer from "./MaterialCategoriesContainer";

  export default {
    name: "MaterialBaseContainer",

    components: {
      MaterialCategoriesContainer,
      MaterialCategories,
      MaterialViewSettingsMenu,
      AppTag,
    },

    props: {
      activeCategory: {
        type: Array,
        default: () => [],
      },
      gutterSize: {
        type: Number,
        default: 16,
      },
      mandatoryCategory: {
        type: Boolean,
        default: true,
      },
      search: {
        type: String,
        default: undefined,
      },
      searchTag: {
        type: String,
        default: undefined,
      },
    },

    data() {
      return {
        dontUpdateQuery: true,
        collapsedCategories: false,
        searchV: undefined,

        categories: [],
        loadingCategories: true,
        categoriesNext: "",
        activeCategoryV: [],
        materialsTotalOverall: undefined,
        categoriesOffsetRight: 0,
        cancelCategories: undefined,
        cancelCategoriesNext: undefined,
        loadingKey: 1,


        drawerIsOpen: false,
        group: null
      };
    },

    computed: {
      zoomActive() {
        return this.$route?.meta?.isZoomAvailable;
      },
    },

    watch: {
      activeCategoryV: 'trackCategory',

      group() { this.drawerIsOpen = false },

      search: {
        immediate: true,
        handler(v) {
          if (v === this.searchV) return;
          this.searchV = v;
        }
      },

      drawerIsOpen: {
        immediate: false,
        handler(v) {
          this.$bus.$emit('navigation:is-open', v);
        }
      },

      activeCategory: {
        deep: true,
        immediate: true,
        handler(v) {
          const { categories, activeCategoryV } = this;

          if (v.length === 0) {
            this.activeCategoryV = [];
            if (categories.length === 0) this.fetchCategories();
            return;
          }
          if (arraysUtils.equal(v, activeCategoryV)) return;

          const category = v[0];
          let newCategories = [ category ];
          this.loadCategoriesUntilFound(category)
          .then( () => {
            this.activeCategoryV = newCategories;
          })
          .catch( () => {
            this.$bus.$emit('not-found:active-category');
            this.activeCategoryV = [ this.$config.common.ALL_MATERIALS_TITLE ];
            this.$emit('update:active-category', this.activeCategoryV);
          })
          .finally( () => {
            if (this.categories.length === 0 && !this.loadingCategories) this.fetchCategories();
          });
        }
      },
    },

    created() {
      this.$bus.$on('create:material', this.onCreateMaterial);
      this.$bus.$on('change:material', this.onChangeMaterial);
      this.$bus.$on('delete:material', this.onDeleteMaterial);
      this.$bus.$on('create:category', this.onCreateCategory);
      this.$bus.$on('change:category', this.onChangeCategory);
      this.$bus.$on('delete:category', this.onDeleteCategory);
      this.$bus.$on('navigation:open', this.onMobileMenuCall);
    },

    beforeDestroy() {
      this.$bus.$off('create:material', this.onCreateMaterial);
      this.$bus.$off('change:material', this.onChangeMaterial);
      this.$bus.$off('delete:material', this.onDeleteMaterial);
      this.$bus.$off('create:category', this.onCreateCategory);
      this.$bus.$off('change:category', this.onChangeCategory);
      this.$bus.$off('delete:category', this.onDeleteCategory);
      this.$bus.$off('navigation:open', this.onMobileMenuCall);
    },

    methods: {
      onClickRefresh() {
        this.$bus.$emit('items:refresh');
        this.$emit('click:refresh');
      },

      onToggleCollapse(isCollapsed) {
        if (this.$vuetify.breakpoint.xsOnly) {
          this.drawerIsOpen = isCollapsed;
        } else {
          this.collapsedCategories = isCollapsed;
          this.$bus.$emit('collapse:categories');
        }
      },

      onCreateMaterial() {
        this.fetchMaterialsTotalCount();
      },

      onMobileMenuCall() {
        this.drawerIsOpen = true;
      },

      onChangeMaterial({ old, saved }) {
        const PUBLISHED_KEY = this.$enums.Status.PUBLISHED.key;
        if ([saved, old].filter( it => it.status === PUBLISHED_KEY).length === 1) {
          if (old.status === PUBLISHED_KEY) this.materialsTotalOverall -= 1;
          if (saved.status === PUBLISHED_KEY) this.materialsTotalOverall += 1;
          this.updateCategory(old.category);
          this.updateCategory(saved.category);
        }
      },

      onChangeZoomActive(value) {
        this.zoomActive = value;
      },

      onDeleteMaterial() {
        this.fetchMaterialsTotalCount();
      },

      onCreateCategory(category) {
        if (this.categoriesNext) return;
        this.categories.push(category);
      },

      onChangeCategory({ saved }) {
        this.updateCategory(saved?.id);
      },

      onDeleteCategory({ id }) {
        if (!id) return;
        let index = this.categories.findIndex( it => it.id === id);
        if (index >=0) this.categories.splice(index, 1);
      },

      resetSearchTag() {
        this.$emit('update:search-tag', undefined);
      },

      async loadCategoriesUntilFound(title) {
        if (!title) return;
        const { categories } = this;
        return new Promise( (resolve, reject) => {
          if (title === this.$config.common.ALL_MATERIALS_TITLE) return resolve();
          if (categories.some( it => it.title === title )) return resolve();
          this.fetchAllCategories()
          .then( () => {
            if (this.categories.find( item => item.title === title )) resolve();
            else {
              this.$store.dispatch('notification/error', `Cannot find category ${title}`);
              reject();
            }
          });
        });
      },

      async updateCategory(id) {
        if (!id) return;
        if (!this.categories.some( item => item.id === id )) return;

        this.fetchCategory(id)
        .then( result => {
          let index = this.categories.findIndex( item => item.id === id );
          if (index < 0) return;
          this.$set(this.categories, index, result);
        });
      },

      async fetchCategory(id) {
        return this.$api.categories.getData(id);
      },

      async fetchAllCategories() {
        this.loadingCategories = true;
        this.loadingCategoriesAll = true;
        const { getAll, getNext } = this.$api.categories;
        this.categoriesNext = null;
        if (this.cancelCategories) this.cancelCategories();
        if (this.cancelCategoriesNext) this.cancelCategoriesNext();
        this.categories = [];

        return requestsUtils.fetchAllList({
          getAll,
          getNext,
          target: 'categories',
          targetLoading: 'loadingCategories',
          targetCancel: 'cancelCategories',
          targetLoadingKey: 'loadingKey',
          targetObject: this,
        })
        .then( () => {
          this.fetchMaterialsTotalCount();
        });
      },

      async fetchCategories() {
        if (this.loadingCategoriesAll) return Promise.resolve();
        this.loadingCategories = true;
        if (this.cancelCategories) this.cancelCategories();
        if (this.cancelCategoriesNext) this.cancelCategoriesNext();
        this.categories = [];

        return requestsUtils.fetchAllStart({
          getAll: this.$api.categories.getAll,
          targetObject: this,
          mapPropertiesObject: {
            results: 'categories',
          },
          targetNext: 'categoriesNext',
          targetLoading: 'loadingCategories',
          targetCancel: 'cancelCategories',
          targetLoadingKey: 'loadingKey',
          loadingKey: this.loadingKey,
        })
        .then( () => {
          this.fetchMaterialsTotalCount();
        })
        .catch( () => {
          this.categories = [];
        });
      },

      fetchCategoriesNext() {
        if (this.loadingCategoriesAll) return Promise.resolve();
        return requestsUtils.fetchAllNext({
          getNext: this.$api.categories.getNext,
          targetObject: this,
          pushPropertiesObject: {
            results: 'categories',
          },
          targetNext: 'categoriesNext',
          targetLoading: 'loadingCategories',
          targetCancel: 'cancelCategoriesNext',
          loadingKey: this.loadingKey,
          targetLoadingKey: 'loadingKey',
        })
      },

      async fetchMaterialsTotalCount() {
        this.materialsTotalOverall = await this.$api.materials.countNumber({
          status: this.$enums.Status.PUBLISHED.key
        });
      },

      trackCategory() {
        const { activeCategoryV } = this;
        let title = activeCategoryV[0];
        this.$gtag.event('select_category', { title });
      },
    },
  }
</script>
